/**
 * 「报告平台 Bug」入口 —— 通往 Nova UAT Platform 的上报页。
 *
 * ⚠️ **和业务上的「report issue」不是一回事。**
 * 这个系统里已经有好几种「上报」：报房屋维修（report maintenance）、
 * 报客诉、开 service request。那些是**业务数据**，走 main-api。
 * 这一条报的是**这套软件本身坏了** —— 页面白屏、按钮点不动、数字不对，
 * 收件方是 QA/开发，不是运营。所以界面文案一律写「Bug / 平台」，
 * 不要只写 "Report Issue"：两者混起来的代价是一线员工把维修单报进了
 * QA 队列，或者反过来。
 *
 * 我们**不在这边做上报页面**。UAT 平台上已经有一个（多语言、能传截图、
 * 能查自己报过的历史），这里只负责把当前登录用户的身份带过去。
 * 详见 nova-uat-platform 的 docs/USER-REPORTING.md §2.3。
 */

/**
 * 上报站点的地址。写法对齐 `novu.config.ts`：env 覆盖 + 生产默认值。
 *
 * 默认值是**正式域名** `report-bug.novavacation.com`，不是当初那个
 * `nova-uat-platform.vercel.app` —— 后者是搭起来时的临时地址，已经换掉。
 * 域名本身也是文案的一部分：一线员工点开之后看到地址栏里写着 report-bug，
 * 就不会以为自己进错了地方（这跟按钮为什么叫 "App Bug" 是同一件事）。
 *
 * 开发时用 `VITE_UAT_REPORT_BASE` 指到本地那台，例如
 * `VITE_UAT_REPORT_BASE=http://localhost:3000`。
 */
export const REPORT_ISSUE_BASE: string =
  import.meta.env.VITE_UAT_REPORT_BASE || 'https://report-bug.novavacation.com'

export interface ReportIssueParams {
  /** 当前登录用户的 id（`state.user.userId`）——就是 main-api users 表那个 id。 */
  userId?: string | null
  /** 显示名，只用来预填上报表单的名字栏，人可以改。 */
  name?: string | null
  /** 覆盖基地址。给测试用；运行时走 REPORT_ISSUE_BASE。 */
  base?: string
}

/**
 * 拼出上报页地址。
 *
 * **不带语言段** —— UAT 平台的 proxy 会按浏览器的 Accept-Language 补上，
 * 一线员工不必看懂中文界面，我们也不用为此维护四条链接。
 *
 * `uid` / `name` 缺了就不发那个参数：发一个空的 uid 过去只会在 UAT 的日志里
 * 留下一条「带了身份但没能用」的告警，而这里本来就没有身份。
 */
export function buildReportIssueUrl({ userId, name, base }: ReportIssueParams = {}): string {
  const root = (base ?? REPORT_ISSUE_BASE).replace(/\/+$/, '')
  const params = new URLSearchParams({ from: 'web' })

  const uid = userId?.trim()
  if (uid) params.set('uid', uid)

  const who = name?.trim()
  if (who) params.set('name', who)

  return `${root}/report?${params.toString()}`
}

/**
 * 在新标签页打开上报页。
 *
 * **必须开新标签，不能原地跳转。** 人是在撞见 bug 的那一刻点这个按钮的——
 * 当前页面上那个坏掉的状态（填了一半的表单、复现到一半的步骤）就是最值钱
 * 的证据。跳走等于把它毁掉，然后让他凭记忆去描述。
 */
export function openReportIssue(params: ReportIssueParams = {}): void {
  window.open(buildReportIssueUrl(params), '_blank', 'noopener,noreferrer')
}
